import {
  DAILY_REDEMPTION_LIMIT_CODE,
  MONTHLY_PARTICIPATION_LIMIT_CODE,
  requestOtp,
} from '@/api/otp';
import { validateUniqueCode } from '@/api/uniqueCode';
import MetallicPillButton from '@/components/Base/MetallicPillButton';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import { SurveyBrownPanel, SurveyBrownPanelInset } from '@/components/layout/SurveyBrownPanel';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import { normalizeCodeBrand } from '@/config/codeBrand';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { TURNSTILE_CONTAINER_CLASS, useTurnstile } from '@/hooks/useTurnstile';
import { useTranslation } from '@/hooks/useTranslation';
import { performFullNewCustomerReset } from '@/lib/performFullNewCustomerReset';
import { useAppDispatch, useAppSelector } from '@/stores/hooks';
import { setUniqueCodeDraft } from '@/stores/appSlice';
import { selectSurveyScreening, selectUniqueCodeDraft } from '@/stores/selectors';
import { displayFullMalaysiaMobile, formatLocalDigits, parseLocalMobileInput } from '@/utils/malaysiaMobile';
import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

const lightInputClassName =
  'min-w-0 w-full rounded-sm border border-slate-300/90 bg-white px-3 py-2.5 text-[16px] font-semibold text-slate-900 placeholder:font-normal placeholder:text-slate-400 outline-none ring-0 focus:border-slate-400 focus:ring-1 focus:ring-slate-300';

const mobileFieldShell =
  'flex min-w-0 w-full overflow-hidden rounded-sm border border-slate-300/90 bg-white focus-within:border-slate-400 focus-within:ring-1 focus-within:ring-slate-300';

const mobilePrefixClass =
  'flex shrink-0 items-center border-r border-slate-300/90 bg-slate-100 px-2.5 py-2.5 text-[16px] font-semibold tabular-nums text-slate-800';

const mobileLocalInputClass =
  'min-w-0 flex-1 border-0 bg-transparent px-3 py-2.5 text-[16px] font-semibold text-slate-900 placeholder:font-normal placeholder:text-slate-400 outline-none focus:ring-0 tabular-nums';

export default function UniqueCode() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const { mobileLocalDigits, uniqueCode, codeBrand, customerUuid, otpSentAtMs } =
    useAppSelector(selectUniqueCodeDraft);
  const { remountTurnstile, executeTurnstile, turnstileRef, turnstileEnabled } = useTurnstile();
  const [uniqueCodeWarning, setUniqueCodeWarning] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifyingSecurity, setIsVerifyingSecurity] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showMonthlyLimitModal, setShowMonthlyLimitModal] = useState(false);
  /** Prevents overlapping confirm + Turnstile runs (same-frame double tap corrupts token callback). */
  const otpConfirmInFlightRef = useRef(false);

  useEffect(() => {
    setUniqueCodeWarning(null);
  }, [uniqueCode, mobileLocalDigits]);

  useEffect(() => {
    if (!showConfirmModal && !showMonthlyLimitModal) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (showMonthlyLimitModal) return;
      setShowConfirmModal(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showConfirmModal, showMonthlyLimitModal]);

  const handleMonthlyLimitClose = () => {
    setShowMonthlyLimitModal(false);
    performFullNewCustomerReset();
    navigate('/', { replace: true });
  };

  const patchDraft = (partial: {
    mobileLocalDigits?: string;
    uniqueCode?: string;
    codeBrand?: typeof codeBrand;
  }) => {
    const nextMobile = partial.mobileLocalDigits ?? mobileLocalDigits;
    const nextCode = partial.uniqueCode ?? uniqueCode;
    const clearSession =
      partial.mobileLocalDigits !== undefined || partial.uniqueCode !== undefined;
    dispatch(
      setUniqueCodeDraft({
        mobileLocalDigits: nextMobile,
        uniqueCode: nextCode,
        codeBrand: clearSession ? undefined : (partial.codeBrand ?? codeBrand),
        customerUuid: clearSession ? undefined : customerUuid,
        ...(!clearSession && otpSentAtMs != null ? { otpSentAtMs } : {}),
      })
    );
  };

  if (surveyLanguage == null) {
    return <Navigate to="/" replace />;
  }
  if (surveyScreening == null) {
    return <Navigate to="/age_smoker" replace />;
  }

  const mobileDigits = mobileLocalDigits.replace(/\D/g, '');
  const hasMobileNo = mobileDigits.length > 0;

  const openRequestOtpConfirm = async () => {
    if (!hasMobileNo) {
      setUniqueCodeWarning(t.uniqueCode.mobileWarning);
      return;
    }

    const code = uniqueCode.trim();
    if (!code) {
      setUniqueCodeWarning(t.uniqueCode.uniqueCodeWarning);
      return;
    }

    setIsSubmitting(true);
    setUniqueCodeWarning(null);
    try {
      const result = await validateUniqueCode(code);
      if (!result.valid) {
        setUniqueCodeWarning(result.message ?? t.uniqueCode.codeNotFoundWarning);
        return;
      }
      if (result.codeUsed) {
        setUniqueCodeWarning(t.uniqueCode.codeUsedWarning);
        return;
      }
      const brand = normalizeCodeBrand(result.codeBrand);
      dispatch(
        setUniqueCodeDraft({
          mobileLocalDigits,
          uniqueCode,
          codeBrand: brand,
          customerUuid: undefined,
        })
      );
      setShowConfirmModal(true);
    } catch {
      setUniqueCodeWarning(t.uniqueCode.verifyError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmRequestOtp = async () => {
    if (otpConfirmInFlightRef.current) {
      return;
    }
    otpConfirmInFlightRef.current = true;
    setIsSubmitting(true);
    let turnstileToken: string | null = null;
    /** If we navigate to /otp, unmount will `remove` the widget — skip remount to avoid a pointless render. */
    let navigatedToOtp = false;
    try {
      if (turnstileEnabled) {
        setIsVerifyingSecurity(true);
        turnstileToken = await executeTurnstile();
        setIsVerifyingSecurity(false);
        if (!turnstileToken) {
          setUniqueCodeWarning(t.uniqueCode.turnstileFailed);
          return;
        }
      }
      const code = uniqueCode.trim();
      setUniqueCodeWarning(null);
      try {
        const result = await requestOtp({
          uniqueCode: code,
          mobileLocalDigits: mobileDigits,
          ageBand: surveyScreening.ageBand,
          language: surveyLanguage,
          turnstileToken,
        });
        if (result.ok) {
          setShowConfirmModal(false);
          dispatch(
            setUniqueCodeDraft({
              mobileLocalDigits,
              uniqueCode,
              codeBrand: codeBrand ?? normalizeCodeBrand(null),
              customerUuid: result.customerUuid,
              otpSentAtMs: Date.now(),
            })
          );
          navigatedToOtp = true;
          navigate('/otp');
          return;
        }
        setShowConfirmModal(false);
        if (result.code === MONTHLY_PARTICIPATION_LIMIT_CODE) {
          setShowMonthlyLimitModal(true);
          return;
        }
        if (result.code === DAILY_REDEMPTION_LIMIT_CODE) {
          setUniqueCodeWarning(t.uniqueCode.dailyLimitMessage);
          return;
        }
        if (result.status === 404) {
          setUniqueCodeWarning(t.uniqueCode.codeNotFoundWarning);
          return;
        }
        if (result.status === 409) {
          setUniqueCodeWarning(result.error || t.uniqueCode.codeUsedWarning);
          return;
        }
        setUniqueCodeWarning(result.error);
      } catch {
        setShowConfirmModal(false);
        setUniqueCodeWarning(t.uniqueCode.otpError);
      }
    } finally {
      setIsVerifyingSecurity(false);
      setIsSubmitting(false);
      otpConfirmInFlightRef.current = false;
      if (turnstileEnabled && !navigatedToOtp) {
        remountTurnstile();
      }
    }
  };

  return (
    <SurveyBackgroundLayout>
      <div ref={turnstileRef} className={TURNSTILE_CONTAINER_CLASS} aria-hidden="true" />
      <div className="relative z-10 flex min-h-0 flex-1 flex-col overflow-y-auto">
        <div className="mx-auto mt-[25vh] flex w-full max-w-full flex-col py-8">
          <SurveyBrownPanelInset>
            <p className="m-0 text-left text-[16px] font-normal leading-snug text-white/95 sm:text-[17px]">
              {t.uniqueCode.instruction}
            </p>
          </SurveyBrownPanelInset>

          <SurveyBrownPanelInset className="mt-6">
            <SurveyBrownPanel className="!p-4">
              <div className="flex flex-col gap-3">
                <div className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2 gap-y-5 sm:gap-x-3">
                  <label
                    htmlFor="mobile-local"
                    className="text-left text-[16px] font-medium text-white/95 sm:text-[17px]"
                  >
                    {t.uniqueCode.mobileLabel}
                  </label>
                  <div className={mobileFieldShell}>
                    <span className={mobilePrefixClass} aria-hidden>
                      +60
                    </span>
                    <input
                      id="mobile-local"
                      type="tel"
                      name="mobile"
                      inputMode="numeric"
                      autoComplete="tel-national"
                      placeholder="12 345 6789"
                      value={formatLocalDigits(mobileLocalDigits)}
                      onChange={(e) =>
                        patchDraft({ mobileLocalDigits: parseLocalMobileInput(e.target.value) })
                      }
                      className={mobileLocalInputClass}
                    />
                  </div>
                  <label
                    htmlFor="unique-code"
                    className="text-left text-[16px] font-medium text-white/95 sm:text-[17px]"
                  >
                    {t.uniqueCode.uniqueCodeLabel}
                  </label>
                  <input
                    id="unique-code"
                    type="text"
                    name="uniqueCode"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder=""
                    value={uniqueCode}
                    onChange={(e) => patchDraft({ uniqueCode: e.target.value.toUpperCase() })}
                    className={`${lightInputClassName} tracking-wide uppercase`}
                  />
                </div>
                <p className="m-0 text-left text-[14px] italic leading-snug text-white/85 sm:text-[15px]">
                  {t.uniqueCode.findCodeHint}
                </p>
              </div>
            </SurveyBrownPanel>
          </SurveyBrownPanelInset>

          <SurveyBrownPanelInset className="mt-10">
            <div className="flex flex-col items-center gap-3">
              {uniqueCodeWarning != null ? (
                <SurveyValidationAlert>{uniqueCodeWarning}</SurveyValidationAlert>
              ) : null}
              <MetallicPillButton
                type="button"
                size="sm"
                className="metallic-pill--lang-home"
                disabled={isSubmitting || !hasMobileNo}
                onClick={() => void openRequestOtpConfirm()}
                title={!hasMobileNo ? t.uniqueCode.mobileWarning : undefined}
              >
                {t.uniqueCode.requestOtpButton}
              </MetallicPillButton>
            </div>
          </SurveyBrownPanelInset>
        </div>
      </div>

      {showConfirmModal ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/55 py-8"
          role="presentation"
        >
          <div
            className="relative mx-2 w-[calc(100vw-1rem)] max-w-none rounded-lg border border-white/15 bg-[#010B1F]/95 px-7 py-10 shadow-xl sm:mx-3 sm:w-[calc(100vw-1.5rem)] sm:px-9 sm:py-12"
            role="dialog"
            aria-modal="true"
            aria-labelledby="otp-confirm-title"
          >
            <p id="otp-confirm-title" className="m-0 text-left text-[16px] font-normal leading-relaxed text-white/95 sm:text-[17px]">
              {t.uniqueCode.confirmTitle}
            </p>
            <ul className="mt-3 list-none space-y-2 p-0 text-left text-[15px] text-white/90 sm:text-[16px]">
              <li>
                <span className="font-medium text-white/95">{t.uniqueCode.confirmMobileLabel}</span>
                <span className="font-semibold tabular-nums">{displayFullMalaysiaMobile(mobileLocalDigits)}</span>
              </li>
              <li>
                <span className="font-medium text-white/95">{t.uniqueCode.confirmUniqueCodeLabel}</span>
                <span className="font-semibold tracking-wide">{uniqueCode.trim() || '—'}</span>
              </li>
            </ul>
            <p className="mt-4 m-0 text-left text-[15px] leading-snug text-white/90 sm:text-[16px]">
              {t.uniqueCode.confirmPrompt}
            </p>
            {isVerifyingSecurity ? (
              <p className="mt-3 m-0 text-left text-[14px] leading-snug text-sky-100/95 sm:text-[15px] animate-pulse">
                {t.uniqueCode.verifyingSecurity}
              </p>
            ) : null}
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <MetallicPillButton
                type="button"
                size="sm"
                className="metallic-pill--lang-home"
                disabled={isSubmitting}
                onClick={() => setShowConfirmModal(false)}
              >
                {t.uniqueCode.back}
              </MetallicPillButton>
              <MetallicPillButton
                type="button"
                size="sm"
                className="metallic-pill--lang-home"
                disabled={isSubmitting}
                onClick={() => void handleConfirmRequestOtp()}
              >
                {t.uniqueCode.confirm}
              </MetallicPillButton>
            </div>
          </div>
        </div>
      ) : null}

      {showMonthlyLimitModal ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/55 py-8"
          role="presentation"
        >
          <div
            className="relative mx-2 flex min-h-[220px] w-[calc(100vw-1rem)] max-w-none flex-col justify-center rounded-lg border border-white/15 bg-[#010B1F]/95 px-7 py-10 shadow-xl sm:mx-3 sm:min-h-[260px] sm:w-[calc(100vw-1.5rem)] sm:px-9 sm:py-12"
            role="dialog"
            aria-modal="true"
            aria-labelledby="monthly-limit-title"
          >
            <p
              id="monthly-limit-title"
              className="m-0 text-left text-[16px] font-normal leading-relaxed text-white/95 sm:text-[17px]"
            >
              {t.uniqueCode.monthlyLimitMessage}
            </p>
            <div className="mt-10 flex items-center justify-center">
              <MetallicPillButton
                type="button"
                size="sm"
                className="metallic-pill--lang-home"
                onClick={handleMonthlyLimitClose}
              >
                {t.uniqueCode.monthlyLimitClose}
              </MetallicPillButton>
            </div>
          </div>
        </div>
      ) : null}
    </SurveyBackgroundLayout>
  );
}
